import { PictureOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Upload,
  ColorPicker,
  Space,
  Typography,
} from "antd";
import { IMAGE_URL } from "constants/ApiConstant";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "store/slices/modalsSlice";
import {
  createSchool,
  getSchoolById,
  getSchools,
  updateSchoolById,
} from "store/slices/schoolsSlice";

const { Text } = Typography;
const currencies = ["USD", "EUR", "DZD"];

const NewSchoolModal = () => {
  const { t } = useTranslation();
  const [form] = Form?.useForm();
  const dispatch = useDispatch();
  const { isModalOpen, modalType, modalProps } = useSelector(
    (state) => state?.modals
  );
  const { school, loading } = useSelector((state) => state?.schools);
  const { userData } = useSelector((state) => state?.auth);
  const open = isModalOpen && modalType === "new_school";
  const [graphicCharter, setGraphicCharter] = useState([]);

  useEffect(() => {
    if (open) dispatch(getSchools(userData?._id));
  }, [dispatch, open, userData?._id]);

  useEffect(() => {
    if (modalProps?.schoolId && open) {
      dispatch(
        getSchoolById({
          adminSchoolId: userData?._id,
          schoolId: modalProps?.schoolId,
        })
      );
    }
  }, [dispatch, modalProps?.schoolId, open, userData?._id]);

  useEffect(() => {
    if (school && open) {
      form?.setFieldsValue({
        ...school,
        logo: null,
      });

      if (school?.logo?.path) {
        form?.setFieldsValue({
          logo: [
            {
              uid: "-1",
              name: "logo.png",
              status: "done",
              url: `${IMAGE_URL}/${school?.logo?.path}`,
            },
          ],
        });
      }

      // Set graphic charter colors
      if (school?.graphicCharter && Array.isArray(school?.graphicCharter)) {
        setGraphicCharter(school?.graphicCharter);
      } else {
        setGraphicCharter([]);
      }
    } else if (!school) {
      form?.resetFields();
      setGraphicCharter([]);
    }
  }, [form, school, open]);

  const normFile = (e) => {
    if (Array?.isArray(e)) {
      return e;
    }
    return e && e?.fileList;
  };
  const onSubmit = async (values) => {
    if (values?.logo) {
      values.logo = values?.logo[0]?.originFileObj;
    }

    const formData = new FormData();

    console?.log("Form values", values);

    // Add graphic charter data if we're updating
    if (modalProps?.schoolId && graphicCharter.length > 0) {
      graphicCharter.forEach((item, index) => {
        formData?.append(`graphicCharter[${index}][color]`, item.color);
      });
    }

    for (const key in values) {
      console?.log(key, values[key]);
      formData?.append(key, values[key]);
    }

    console?.log("Form data", formData);

    try {
      if (!modalProps?.schoolId)
        await dispatch(
          createSchool({
            adminSchoolId: userData?._id,
            data: formData,
          })
        )?.unwrap();
      else
        await dispatch(
          updateSchoolById({
            adminSchoolId: userData?._id,
            schoolId: modalProps?.schoolId,
            data: formData,
          })
        )?.unwrap();
      onClose();
    } catch (error) {
      notification?.error({
        message: t("schools.crud.form.error.failed"),
        description: error,
        role: "alert",
        duration: 3,
      });
    }
  };

  const onClose = () => {
    form?.resetFields();
    setGraphicCharter([]);
    dispatch(closeModal());
  };

  const handleColorChange = (index, color) => {
    const newGraphicCharter = [...graphicCharter];
    newGraphicCharter[index] = { color: color.toHexString() };
    setGraphicCharter(newGraphicCharter);
  };

  const handleAddColor = () => {
    if (graphicCharter.length < 2) {
      setGraphicCharter([...graphicCharter, { color: "#79389F" }]);
    }
  };

  const handleRemoveColor = (index) => {
    const newGraphicCharter = graphicCharter.filter((_, i) => i !== index);
    setGraphicCharter(newGraphicCharter);
  };
  
  return (
    <Modal
      title={<h4 style={{ textAlign: "center" }}>New School</h4>}
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("schools.crud.form.button.cancel")}
        </Button>,
        <Button
          key="submit"
          type="primary"
          htmlType="submit"
          form="new_school_form"
          disabled={loading}
        >
          {t("schools.crud.form.button.save")}
        </Button>,
      ]}
      centered
      style={{
        top: 20,
      }}
      bodyStyle={{
        maxHeight: "75vh",
        overflowY: "auto",
      }}
    >
      <Form
        layout="vertical"
        name="new_school"
        id="new_school_form"
        form={form}
        onFinish={onSubmit}
        disabled={loading}
        style={{
          maxWidth: 600,
          margin: "0 auto",
        }}
      >
        <Form.Item
          name="logo"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          style={{ textAlign: "center" }}
          rules={[
            {
              required: false,
              message: t("schools.crud.form.error.label.required.logo"),
            },
          ]}
        >
          <Upload
            name="logo"
            listType="picture-circle"
            maxCount={1}
            progress={{ strokeWidth: 2, showInfo: false }}
            beforeUpload={() => false}
          >
            <Row span={24} align="middle" justify="center">
              <PictureOutlined style={{ fontSize: "24px" }} />
              {t("schools.crud.form.button.upload")}
            </Row>
          </Upload>
        </Form.Item>

        <Form.Item
          label={t("schools.crud.form.label.name")}
          name="name"
          rules={[
            {
              required: true,
              message: t("schools.crud.form.error.label.required.name"),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={t("schools.crud.form.label.email")}
          name="email"
          rules={[
            {
              required: true,
              message: t("schools.crud.form.error.label.required.email"),
              type: "email",
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={t("schools.crud.form.label.phone")}
          name="phoneNumber"
          rules={[
            {
              required: true,
              message: t("schools.crud.form.error.label.required.phone"),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={t("schools.crud.form.label.website")}
          name="webSite"
          rules={[
            {
              required: false,
              message: t("schools.crud.form.error.label.required.website"),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={t("schools.crud.form.label.currency")}
          name="currency"
          rules={[
            {
              required: true,
              message: t("schools.crud.form.error.label.required.currency"),
            },
          ]}
        >
          <Select>
            {currencies?.map((currency) => (
              <Select.Option key={currency} value={currency}>
                {currency}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={t("schools.crud.form.label.address")}
          name="address"
          rules={[
            {
              required: true,
              message: t("schools.crud.form.error.label.required.address"),
            },
          ]}
        >
          <Input.TextArea rows={4} />
        </Form.Item>

        {/* Graphic Charter - Only show when updating */}
        {modalProps?.schoolId && (
          <Form.Item
            label={t("schools.crud.form.label.graphicCharter", "Charte graphique")}
          >
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              {graphicCharter.map((item, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    padding: "12px",
                    border: "1px solid #d9d9d9",
                    borderRadius: "8px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <ColorPicker
                    value={item.color}
                    onChange={(color) => handleColorChange(index, color)}
                    showText
                    style={{ width: "180px" }}
                  />
                 
                  <Button
                    danger
                    size="small"
                    onClick={() => handleRemoveColor(index)}
                    disabled={loading}
                  >
                    {t("schools.crud.form.button.remove", "Supprimer")}
                  </Button>
                </div>
              ))}
              <Button
                type="dashed"
                onClick={handleAddColor}
                block
                disabled={loading || graphicCharter.length >= 2}
              >
                {t("schools.crud.form.button.addColor", "Ajouter une couleur")}
              </Button>
            </Space>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default NewSchoolModal;
